import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend,
} from "recharts";
import { motion } from "framer-motion";
import {
  CalendarDays, IndianRupee, UserPlus, Users, Stethoscope, FileText,
  UserCog, HeartPulse, Briefcase, AlertCircle, ReceiptText, BedDouble, LogOut, FlaskConical,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";

const COLORS = ["#0369A1", "#38BDF8", "#0E7490", "#F59E0B", "#16A34A", "#7C3AED", "#DC2626", "#78716C"];

const CARDS = [
  ["today_appointments", "Today's appointments", CalendarDays],
  ["today_revenue", "Today's revenue", IndianRupee, true],
  ["today_patients", "New patients today", UserPlus],
  ["total_patients", "Total patients", Users],
  ["consultations", "Consultations", Stethoscope],
  ["prescriptions", "Prescriptions", FileText],
  ["doctors", "Doctors", UserCog],
  ["nurses", "Nurses", HeartPulse],
  ["staff", "Total staff", Briefcase],
  ["pending_bills", "Outstanding dues", AlertCircle, true],
  ["total_bills", "Bills raised", ReceiptText],
  ["admissions", "Current admissions", BedDouble],
  ["discharges", "Discharges", LogOut],
  ["investigations", "Investigations", FlaskConical],
];

function ChartCard({ title, children, testid }) {
  return (
    <Card data-testid={testid} className="shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent className="h-[260px] pt-2">
        <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

const axis = { stroke: "hsl(var(--muted-foreground))", fontSize: 11 };
const tip = { contentStyle: { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 } };

export default function Dashboard() {
  const { fq, facilities, facilityId, user } = useApp();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get(`/dashboard${fq ? `?${fq}` : ""}`).then(({ data }) => setD(data)).catch(() => {});
  }, [fq]);

  const scope = facilityId === "all"
    ? "Combined — hospital + clinics"
    : facilities.find((f) => String(f.id) === String(facilityId))?.name || "";

  if (!d) return <p className="text-sm text-muted-foreground">Loading clinical overview…</p>;

  return (
    <div className="space-y-7" data-testid="dashboard-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-primary">{scope}</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Operations overview</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Signed in as {user.full_name} · {user.role.replace("_", " ")}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
        {CARDS.map(([key, label, Icon, money], i) => (
          <motion.div key={key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}>
            <Card data-testid={`stat-${key}`} className="shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <p className="text-xs text-muted-foreground leading-snug pr-2">{label}</p>
                  <Icon className="h-4 w-4 text-primary shrink-0" />
                </div>
                <p className="font-heading text-2xl font-bold mt-2 tabular-nums">
                  {money ? `₹${Number(d.cards[key]).toLocaleString("en-IN")}` : d.cards[key]}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="Monthly revenue (12 months)" testid="chart-monthly-revenue">
          <AreaChart data={d.monthly_revenue}>
            <defs>
              <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0369A1" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#0369A1" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="month" {...axis} /><YAxis {...axis} />
            <Tooltip {...tip} />
            <Area type="monotone" dataKey="revenue" stroke="#0369A1" fill="url(#rev)" strokeWidth={2} />
          </AreaChart>
        </ChartCard>

        <ChartCard title="Patient registrations (14 days)" testid="chart-patient-trend">
          <LineChart data={d.patient_trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="date" {...axis} /><YAxis {...axis} allowDecimals={false} />
            <Tooltip {...tip} />
            <Line type="monotone" dataKey="patients" stroke="#0E7490" strokeWidth={2} dot={false} />
          </LineChart>
        </ChartCard>

        <ChartCard title="Hospital vs clinic performance" testid="chart-facility-comparison">
          <BarChart data={d.facility_comparison}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="name" {...axis} tickFormatter={(v) => v.split(" ").slice(-1)[0]} />
            <YAxis {...axis} /><Tooltip {...tip} /><Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="revenue" fill="#0369A1" radius={[3, 3, 0, 0]} />
            <Bar dataKey="patients" fill="#38BDF8" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ChartCard>

        <ChartCard title="Doctor performance (consultations)" testid="chart-doctor-performance">
          <BarChart data={d.doctor_performance} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
            <XAxis type="number" {...axis} allowDecimals={false} />
            <YAxis type="category" dataKey="name" {...axis} width={130} />
            <Tooltip {...tip} />
            <Bar dataKey="consultations" fill="#0E7490" radius={[0, 3, 3, 0]} />
          </BarChart>
        </ChartCard>

        <ChartCard title="Appointment statistics" testid="chart-appointment-stats">
          <PieChart>
            <Pie data={d.appointment_stats} dataKey="count" nameKey="status" innerRadius={55} outerRadius={90}>
              {d.appointment_stats.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip {...tip} /><Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ChartCard>

        <ChartCard title="Investigation statistics" testid="chart-investigation-stats">
          <BarChart data={d.investigation_stats || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="status" {...axis} /><YAxis {...axis} allowDecimals={false} />
            <Tooltip {...tip} />
            <Bar dataKey="count" fill="#0E7490" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ChartCard>

        <ChartCard title="Top diagnoses" testid="chart-top-diagnoses">
          <BarChart data={d.top_diagnoses}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="name" {...axis} interval={0} height={50} angle={-18} textAnchor="end" />
            <YAxis {...axis} allowDecimals={false} /><Tooltip {...tip} />
            <Bar dataKey="count" fill="#F59E0B" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ChartCard>
      </div>
    </div>
  );
}
